import {
  effect,
  inject,
  Injector,
  makeEnvironmentProviders,
  provideAppInitializer,
  type EnvironmentProviders,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '@env';
import { AuthService } from '@core/auth/auth.service';
import { CompanySessionService } from '@core/auth/company-session.service';
import { AnalyticsService } from './analytics.service';
import { AttributionService } from './attribution.service';
import { ATTRIBUTION_KEYS } from '@typings/app/attribution.types';
import { consentPayload } from './analytics.types';
import type { GtagFn } from './analytics.types';

/**
 * Bootstraps Google Tag Manager: installs the dataLayer + a minimal gtag shim
 * (needed only for Consent Mode, which requires the gtag arguments form), sets
 * Consent Mode v2 BEFORE the container loads, pushes the standard GTM bootstrap
 * event, then injects the container script. GA4 forwarding and the Google Ads
 * conversion tag are configured inside the GTM web UI. Idempotent.
 *
 * Ads signals default to granted so Google Ads conversions are attributed from
 * the first page view; the consent banner and settings toggle revoke them via
 * AnalyticsService.setConsent(). See CONSENT_SIGNALS.
 *
 * `onReady` runs once the container has processed its bootstrap event — by then the
 * Initialization-trigger tags (the Conversion Linker) have run — or when the script
 * fails to load, so callers never wait forever on a blocked container.
 */
export function loadGtm(containerId: string, onReady?: () => void): void {
  if (typeof window === 'undefined' || typeof window.gtag === 'function') return;

  let ready = false;
  const markReady = () => {
    if (ready) return;
    ready = true;
    onReady?.();
  };

  window.dataLayer = window.dataLayer ?? [];
  const gtag: GtagFn = (...args: unknown[]) => {
    window.dataLayer!.push(args);
  };
  window.gtag = gtag;

  gtag('consent', 'default', consentPayload(true));
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js', eventCallback: markReady });

  const script = document.createElement('script');
  script.async = true;
  script.dataset['gtm'] = '';
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  script.addEventListener('error', markReady);
  document.head.appendChild(script);
}

/** Extracts the attribution parameters present in a URL's query string. */
export function readAttributionParams(href: string): URLSearchParams {
  const search = new URL(href).searchParams;
  const ids = new URLSearchParams();
  for (const key of ATTRIBUTION_KEYS) {
    const value = search.get(key);
    if (value) ids.set(key, value);
  }
  return ids;
}

/**
 * Puts the attribution parameters back on the current URL if a navigation dropped them.
 * Only ever adds the keys it owns, so a navigation's own query params (`returnUrl`, `new=1`)
 * are left exactly as the Router set them. Uses replaceState with the existing history
 * state so the Router's navigation bookkeeping is untouched.
 */
export function restoreAttributionParams(ids: URLSearchParams): void {
  const url = new URL(window.location.href);
  let changed = false;
  ids.forEach((value, key) => {
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value);
      changed = true;
    }
  });
  if (changed) window.history.replaceState(window.history.state, '', url);
}

export interface AnalyticsDeps {
  analytics: AnalyticsService;
  attribution: AttributionService;
  router: Router;
  auth: AuthService;
  companySession: CompanySessionService;
  injector: Injector;
}

/** Loads GTM, wires Router page views, and pushes user/company identity via an effect. */
export function initAnalytics(containerId: string, deps: AnalyticsDeps): void {
  // Before the containerId guard on purpose: attribution feeds the conversions endpoint,
  // which is enabled in environments that have no GTM container at all. It is subject to
  // the same race as the click ids below — a guard redirect drops the query string, so the
  // landing parameters have to be taken before the Router's first navigation.
  deps.attribution.capture();

  // GTM loads async, but the Router's first navigation doesn't wait for it — and redirects
  // drop the query string. Without this, an ad landing on `/?gclid=…` is already `/login`
  // by the time the Conversion Linker reads the URL, so the click id never reaches the
  // _gcl_aw cookie. Read here, before the initial navigation, then re-applied after every
  // navigation for the rest of the session — logging in routes to a freshly built URL that
  // carries no query string either, so restoring only until GTM is ready is not enough.
  //
  // Wired before the containerId guard so it also works on dev and staging, which run with
  // no GTM container but still report conversions.
  const attributionParams = readAttributionParams(window.location.href);
  const navigationEnd$ = deps.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd));

  if (attributionParams.size) {
    navigationEnd$.subscribe(() => restoreAttributionParams(attributionParams));
  }

  if (!containerId) return;

  loadGtm(containerId);

  navigationEnd$.subscribe((e) => deps.analytics.pageView(e.urlAfterRedirects, document.title));

  effect(
    () => {
      const employer = deps.auth.employer();
      if (employer) deps.analytics.setUser(employer.id);
      else deps.analytics.clearUser();

      const company = deps.companySession.activeCompany();
      const role = deps.companySession.activeMembership()?.role;
      if (company && role) deps.analytics.setCompanyContext(company.id, String(role));
    },
    { injector: deps.injector },
  );
}

/** Provider entry point — add `provideAnalytics()` to the app.config.ts providers array. */
export function provideAnalytics(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      initAnalytics(environment.googleTagManagerId, {
        analytics: inject(AnalyticsService),
        attribution: inject(AttributionService),
        router: inject(Router),
        auth: inject(AuthService),
        companySession: inject(CompanySessionService),
        injector: inject(Injector),
      });
    }),
  ]);
}
